/**
 * ClaimsTable — DataTable + TableToolbar wrapper for the Claims half
 * of Step 7. No FilterDrawer (claim counts are typically small;
 * search handles filtering). Mirror of GoalsTable minus the filter.
 */
import React, { useEffect, useMemo, useState } from 'react';
import {
  DataTable,
  TableToolbar,
  TablePagination,
  RowActionsMenu,
} from '../../../../components/ui/DataTable';
import { Icon } from '../../../../components/ui/Icon';
import { Button } from '../../../../components/ui/Button2';
import {
  CLAIM_COLUMNS_READ,
  CLAIM_COLUMNS_EDIT,
  CLAIM_ACTIONS_COLUMN_WIDTH,
  type ClaimRecord,
} from './claimsFieldSchema';
import styles from './Step7Tables.module.css';

const DataTableAny = DataTable as unknown as React.ComponentType<any>;
const TableToolbarAny = TableToolbar as unknown as React.ComponentType<any>;
const TablePaginationAny = TablePagination as unknown as React.ComponentType<any>;
const RowActionsMenuAny = RowActionsMenu as unknown as React.ComponentType<any>;
// Button2 is a JSX forwardRef primitive; cast for use from TSX (mirrors the
// ButtonAny convention across the step-components).
const ButtonAny = Button as unknown as React.ComponentType<any>;

const DEFAULT_PAGE_SIZE = 20;

interface SortState {
  column: string;
  direction: 'asc' | 'desc';
}

export interface ClaimsTableProps {
  claims: ClaimRecord[];
  loading?: boolean;
  emptyMessage?: string;
  editable?: boolean;
  onAdd?: () => void;
  onEdit?: (claim: ClaimRecord) => void;
  onDelete?: (claim: ClaimRecord) => void;
  onExport?: () => void;
  onImport?: (file: File) => void;
}

function sortClaims(rows: ClaimRecord[], sort: SortState | null): ClaimRecord[] {
  if (!sort) return rows;
  const dir = sort.direction === 'asc' ? 1 : -1;
  return [...rows].sort((a, b) => {
    const av = (a as Record<string, unknown>)[sort.column];
    const bv = (b as Record<string, unknown>)[sort.column];
    if (av == null && bv == null) return 0;
    if (av == null) return 1;
    if (bv == null) return -1;
    return String(av).localeCompare(String(bv)) * dir;
  });
}

function searchClaims(rows: ClaimRecord[], query: string): ClaimRecord[] {
  const q = query.trim().toLowerCase();
  if (!q) return rows;
  return rows.filter((r) =>
    Object.values(r).some((v) =>
      v == null
        ? false
        : Array.isArray(v)
        ? v.join(' ').toLowerCase().includes(q)
        : String(v).toLowerCase().includes(q),
    ),
  );
}

export default function ClaimsTable({
  claims,
  loading,
  emptyMessage = 'No cybersecurity claims yet.',
  editable = false,
  onAdd,
  onEdit,
  onDelete,
  onExport,
  onImport,
}: ClaimsTableProps) {
  const fileInputRef = React.useRef<HTMLInputElement | null>(null);
  const [search, setSearch] = useState('');
  const [sort, setSort] = useState<SortState | null>(null);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState<number>(DEFAULT_PAGE_SIZE);

  const searched = useMemo(() => searchClaims(claims, search), [claims, search]);
  const sorted = useMemo(() => sortClaims(searched, sort), [searched, sort]);

  const totalPages = Math.max(1, Math.ceil(sorted.length / pageSize));
  useEffect(() => {
    if (page > totalPages) setPage(1);
  }, [page, totalPages]);

  const paged = useMemo(
    () => sorted.slice((page - 1) * pageSize, page * pageSize),
    [sorted, page, pageSize],
  );

  const keyed = useMemo(
    () =>
      paged.map((r, i) => ({
        ...r,
        _rowKey: `${r.claim_id ?? 'row'}::${(page - 1) * pageSize + i}`,
      })),
    [paged, page, pageSize],
  );

  const columns = useMemo(() => {
    if (!editable) return CLAIM_COLUMNS_READ;
    return [
      ...CLAIM_COLUMNS_EDIT,
      {
        key: '_actions',
        label: '',
        align: 'right',
        width: CLAIM_ACTIONS_COLUMN_WIDTH,
        sortable: false,
        render: (row: ClaimRecord) => (
          <RowActionsMenuAny
            ariaLabel={`Actions for ${row.claim_id}`}
            actions={[
              { id: 'edit', label: 'Edit', icon: <Icon name="edit" size={14} />, onClick: () => onEdit?.(row) },
              { id: 'delete', label: 'Delete', icon: <Icon name="delete" size={14} />, danger: true, onClick: () => onDelete?.(row) },
            ]}
          />
        ),
      },
    ];
  }, [editable, onEdit, onDelete]);

  const toolbarActions = (
    <div className={styles.toolbarActions}>
      {onExport && sorted.length > 0 && (
        <ButtonAny variant="outline" size="sm" onClick={onExport}>
          <Icon name="upload" size={14} /> Export CSV
        </ButtonAny>
      )}
      {editable && onImport && (
        <>
          <ButtonAny
            variant="outline"
            size="sm"
            onClick={() => fileInputRef.current?.click()}
          >
            <Icon name="download" size={14} /> Import
          </ButtonAny>
          <input
            ref={fileInputRef}
            type="file"
            accept=".csv,.json"
            style={{ display: 'none' }}
            onChange={(e) => {
              const f = e.target.files?.[0];
              if (f) onImport(f);
              if (e.target) e.target.value = '';
            }}
          />
        </>
      )}
      {editable && onAdd && (
        <ButtonAny variant="default" size="sm" onClick={onAdd}>
          <Icon name="add" size={14} /> Add Claim
        </ButtonAny>
      )}
    </div>
  );

  return (
    <DataTableAny
      columns={columns}
      data={keyed}
      rowKey="_rowKey"
      sort={sort}
      onSortChange={(column: string | null, direction: 'asc' | 'desc' | null) =>
        setSort(column && direction ? { column, direction } : null)
      }
      loading={loading}
      emptyMessage={emptyMessage}
      toolbar={
        <TableToolbarAny
          search={{
            value: search,
            onChange: (v: string) => {
              setSearch(v);
              setPage(1);
            },
            placeholder: 'Search claims…',
          }}
          count={{ value: sorted.length, label: 'Claims' }}
          showFilterButton={false}
          actions={toolbarActions}
        />
      }
      pagination={
        <TablePaginationAny
          currentPage={page}
          totalItems={sorted.length}
          itemsPerPage={pageSize}
          onPageChange={setPage}
          onItemsPerPageChange={(size: number) => {
            setPageSize(size);
            setPage(1);
          }}
        />
      }
    />
  );
}
